<template>
  <div class="card mb-10 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-10 p-sm-15 p-md-20 letter-spacing">
      <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
        <h4 class="position-relative text-black fw-bold mb-10">Fermeture de caisse</h4>
        <router-link to="/OuverturesFermetures" class="btn btn-primary transition border-0 lh-1 fw-medium">
          <i class="flaticon-left-arrow lh-1 me-1 position-relative top-2"></i>
          <span class="position-relative"></span>Retour
        </router-link>
      </div>

      <Form class="row g-2" :validation-schema="schema" @submit="sendFermerOuvFer">
        <div class="row mb-2">
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">Date de fermeture</label>
              <Field
                name="dateFin"
                type="datetime-local"
                :max="currentDateTime"
                v-model="dateFin"
                class="form-control shadow-none fs-md-15 text-black"
                readonly
              />
            </div>
            <ErrorMessage name="dateFin" class="text-danger" />
          </div>

          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Personnes présentes à la fermeture <span class="text-danger">*</span>
              </label>
              <Field name="user" v-slot="{ field }">
                <Multiselect
                  v-model="user"
                  :options="userOptions"
                  :searchable="true"
                  mode="tags"
                  label="label"
                  track-by="value"
                  placeholder="Sélectionner les personnes"
                  v-bind="field"
                />
              </Field>
              <ErrorMessage name="user" class="text-danger" />
              <div v-if="userOptions.length === 0" class="text-danger mt-1 fs-md-12">
                Aucune personne disponible.
              </div>
            </div>
          </div>
        </div>

        <div class="row mb-2">
          <div class="col-md-12">
            <label class="d-block text-black fw-semibold mb-2">Caisse</label>
            <div class="form-control shadow-none fs-md-14 text-black fw-bold">
              Vous êtes en train de fermer la caisse : {{ caisseNom }}
            </div>
            <div v-if="!caisseNom" class="text-danger mt-1 fs-md-12">Aucune caisse ouverte disponible.</div>
          </div>
        </div>

        <!-- Point de caisse (calculé par le serveur à partir du journal) -->
        <div class="row" v-if="rapport">
          <div class="col-md-7">
            <div class="border border-success mb-4">
              <div class="py-1 px-2 fw-bold text-white" style="background-color: #28a745">
                <span class="fs-15">Point de caisse (espèces)</span>
              </div>
              <table class="table table-sm mb-0 align-middle">
                <tbody>
                  <tr>
                    <td>Fond de caisse (ouverture)</td>
                    <td class="text-end fw-semibold">{{ separateur(rapport.fondDeCaisse) }}</td>
                  </tr>
                  <tr>
                    <td>+ Entrées en espèces</td>
                    <td class="text-end fw-semibold text-success">{{ separateur(rapport.totaux.entrees) }}</td>
                  </tr>
                  <tr>
                    <td>− Sorties en espèces</td>
                    <td class="text-end fw-semibold text-danger">{{ separateur(rapport.totaux.sorties) }}</td>
                  </tr>
                  <tr class="table-success">
                    <td class="fw-bold">= Solde théorique</td>
                    <td class="text-end fw-bold">{{ separateur(rapport.soldeTheorique) }} FCFA</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="rapport.remboursementsAnterieurs.length > 0" class="alert alert-info">
              {{ rapport.remboursementsAnterieurs.length }} remboursement(s) concernent des ventes de sessions
              précédentes ({{ separateur(totalRemboursementsAnterieurs) }} FCFA).
            </div>
          </div>

          <div class="col-md-5">
            <div class="border border-primary mb-4">
              <div class="py-1 px-2 fw-bold text-white bg-primary">
                <span class="fs-15">Comptage physique</span>
              </div>
              <div class="p-3">
                <label class="d-block text-black fw-semibold mb-2">
                  Solde compté en caisse (FCFA) <span class="text-danger">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  v-model.number="soldeCompte"
                  :class="soldeCompteInvalide ? 'form-control is-invalid' : 'form-control'"
                  placeholder="Total des espèces comptées"
                />
                <span class="invalid-feedback">Saisissez un montant positif ou nul</span>

                <div class="mt-3 p-2 text-center border" :class="classeEcart">
                  <div class="fs-md-13">Écart (compté − théorique)</div>
                  <div class="fs-5 fw-bold">
                    {{ ecart === null ? '—' : formatEcart(ecart) }}
                  </div>
                  <div class="fs-md-12" v-if="ecart !== null">
                    {{ ecart === 0 ? 'La caisse est juste.' : ecart > 0 ? 'Excédent en caisse.' : 'Manquant en caisse.' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mb-2">
          <div class="col-md-12">
            <label for="observationsFin" class="d-block text-black fw-semibold mb-2">Observations</label>
            <Field
              name="observationsFin"
              as="textarea"
              class="form-control fs-md-14"
              v-model="observationsFin"
              placeholder="Entrez vos observations (facultatif)"
            />
            <ErrorMessage name="observationsFin" class="text-danger fs-md-12" />
          </div>
        </div>

        <div class="mb-2 mt-1">
          <button
            type="submit"
            class="btn btn-primary btn-sm top-end"
            :disabled="!caisse || !rapport || soldeCompteInvalide || isSubmitting"
          >
            Fermer
          </button>
          <router-link to="/OuverturesFermetures" type="button" class="btn btn-danger btn-sm mx-1">
            Annuler
          </router-link>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, defineComponent, onMounted, computed } from "vue";
import * as Yup from "yup";
import { configure, Form, Field, ErrorMessage } from "vee-validate";
import Multiselect from "@vueform/multiselect";
import Swal from "sweetalert2";
import ApiService from "@/services/ApiService";
import router from "@/router";
import { error, separateur } from "@/utils/utils";
import { useRoute } from "vue-router";

interface LigneType {
  sens: "ENTREE" | "SORTIE";
  nombre: number;
  total: number;
}

interface RapportSession {
  fondDeCaisse: number;
  soldeTheorique: number;
  totaux: { entrees: number; sorties: number };
  parType: Record<string, LigneType>;
  remboursementsAnterieurs: { montant: number }[];
}

const LIBELLES_TYPES: Record<string, string> = {
  ENCAISSEMENT_VENTE: "Montant reçu des ventes",
  RENDU_MONNAIE: "Monnaie rendue aux clients",
  REMBOURSEMENT_ANNULATION: "Remboursements (annulations de ventes)",
  ENCAISSEMENT_CREANCE: "Règlements de créances",
  APPORT: "Apports",
  RETRAIT: "Retraits",
  DEPENSE: "Dépenses",
  CORRECTION: "Corrections",
};

export default defineComponent({
  name: "AddFerm",
  components: { Form, Field, ErrorMessage, Multiselect },
  props: {
    id: { type: [Number, String], default: null },
  },

  setup(props) {
    const route = useRoute();

    function getCurrentDateTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    const dateFin = ref(getCurrentDateTime());
    const currentDateTime = ref(getCurrentDateTime());
    const userOptions = ref<{ value: number; label: string }[]>([]);
    const user = ref<number[]>([]);
    const caisse = ref<number | null>(null);
    const caisseNom = ref<string | null>(null);
    const observationsFin = ref<string>("");
    const rapport = ref<RapportSession | null>(null);
    const soldeCompte = ref<number | string>("");
    const isSubmitting = ref(false);
    const formatEcart = (e: number) => `${e > 0 ? "+ " : e < 0 ? "− " : ""}${separateur(Math.abs(e))}`;

    const schema = Yup.object().shape({
      dateFin: Yup.string().required("La date de fermeture est obligatoire"),
      observationsFin: Yup.string().nullable(),
      user: Yup.array().min(1, "Au moins une personne doit être sélectionnée"),
    });

    configure({ validateOnBlur: true, validateOnChange: true, validateOnInput: true });

    // --- Affichage du point de caisse ---------------------------------------
    const lignesDetail = computed(() =>
      Object.entries(rapport.value?.parType ?? {}).map(([type, l]) => ({
        type,
        label: LIBELLES_TYPES[type] ?? type,
        ...l,
      }))
    );

    const totalRemboursementsAnterieurs = computed(() =>
      (rapport.value?.remboursementsAnterieurs ?? []).reduce((t, r) => t + Number(r.montant), 0)
    );

    // --- Comptage et écart ---------------------------------------------------
    const soldeCompteInvalide = computed(
      () => soldeCompte.value === "" || isNaN(Number(soldeCompte.value)) || Number(soldeCompte.value) < 0
    );

    const ecart = computed<number | null>(() => {
      if (!rapport.value || soldeCompteInvalide.value) return null;
      return Math.round((Number(soldeCompte.value) - rapport.value.soldeTheorique) * 100) / 100;
    });

    const classeEcart = computed(() => {
      if (ecart.value === null) return "text-muted";
      return ecart.value === 0 ? "border-success text-success" : "border-danger text-danger";
    });

    // --- Chargement ---------------------------------------------------------
    const checkCaisseStatus = async (caisseId: number) => {
      try {
        const response = await ApiService.get(`/ouvfers/check?caisseId=${caisseId}`);
        if (!response.data.data.isOpen) {
          Swal.fire({
            icon: "warning",
            title: "Caisse non ouverte",
            text: "La caisse n'est pas ouverte. Veuillez ouvrir la caisse avant de la fermer.",
            timer: 3000,
            showConfirmButton: false,
          });
          return false;
        }
        return true;
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors de la vérification de l'état de la caisse.");
        return false;
      }
    };

    const chargerRapport = async (caisseId: number) => {
      try {
        const response = await ApiService.get(`/ouvfers/solde-theorique?caisseId=${caisseId}`);
        rapport.value = response.data.data;
      } catch (err: any) {
        rapport.value = null;
        error(err.response?.data?.message || "Impossible de récupérer le point de caisse.");
      }
    };

    const getOuvFerById = async (ouvFerId: number) => {
      try {
        const { data } = await ApiService.get(`/ouvFer/${ouvFerId}`);
        const ouvFerData = data.data;
        if (ouvFerData && ouvFerData.caisse) {
          caisse.value = ouvFerData.caisse.id;
          caisseNom.value = ouvFerData.caisse.nom;
          if (await checkCaisseStatus(ouvFerData.caisse.id)) {
            await chargerRapport(ouvFerData.caisse.id);
          }
        } else {
          caisse.value = null;
          caisseNom.value = null;
          error("Aucune caisse associée à cette ouverture/fermeture.");
        }
      } catch (err: any) {
        caisse.value = null;
        caisseNom.value = null;
        error(err.response?.data?.message || "Erreur lors de la récupération des données de l'ouverture/fermeture");
      }
    };

    const getAllUsers = async () => {
      try {
        const { data } = await ApiService.get("/users");
        userOptions.value = data.data.data.map((us: any) => ({
          value: us.id,
          label: `${us.nom} ${us.prenom}`,
        }));
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors de la récupération des utilisateurs");
      }
    };

    // --- Fermeture ----------------------------------------------------------
    const sendFermerOuvFer = async () => {
      if (isSubmitting.value) return;
      try {
        if (!caisse.value) throw new Error("Aucune caisse disponible.");
        if (soldeCompteInvalide.value) {
          error("Veuillez saisir le solde compté en caisse.");
          return;
        }
        if (!(await checkCaisseStatus(caisse.value))) return;

        // Le point de caisse a pu changer depuis l'ouverture de la page (ventes en cours)
        await chargerRapport(caisse.value);
        const ecartFinal = ecart.value;

        if (ecartFinal !== null && ecartFinal !== 0) {
          const confirmation = await Swal.fire({
            icon: "warning",
            title: "Écart détecté",
            text: `Un écart de ${formatEcart(ecartFinal)} a été détecté. Voulez-vous fermer la caisse quand même ?`,
            showCancelButton: true,
            confirmButtonText: "Oui, fermer",
            cancelButtonText: "Annuler",
          });
          if (!confirmation.isConfirmed) return;
        }

        isSubmitting.value = true;
        await ApiService.post("/ouvfers/fermer", {
          caisseId: caisse.value,
          soldeFermeture: Number(soldeCompte.value),
          observationsFin: observationsFin.value,
          userIds: user.value,
        });

        Swal.fire({
          icon: "success",
          title: "Succès",
          text: "Fermeture de caisse enregistrée avec succès",
          timer: 2500,
          showConfirmButton: false,
        });
        router.push("/OuverturesFermetures");
      } catch (err: any) {
        error(err?.response?.data?.message || err?.message || "Erreur lors de la fermeture de la caisse.");
      } finally {
        isSubmitting.value = false;
      }
    };

    onMounted(async () => {
      const ouvFerId = Number(route.params.id) || Number(props.id);
      if (ouvFerId) {
        await getOuvFerById(ouvFerId);
      } else {
        error("Aucune ouverture/fermeture spécifiée.");
      }
      await getAllUsers();
    });

    return {
      dateFin,
      currentDateTime,
      user,
      userOptions,
      caisse,
      caisseNom,
      observationsFin,
      rapport,
      soldeCompte,
      soldeCompteInvalide,
      ecart,
      classeEcart,
      lignesDetail,
      totalRemboursementsAnterieurs,
      isSubmitting,
      schema,
      sendFermerOuvFer,
      separateur,
      formatEcart
    };
  },
});
</script>

<style scoped>
.overview {
  margin: auto;
}
</style>